import { StarredFields } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Control, SetFieldValue, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useUpdateIdentityMutation, useUpdateRelatedPartiesMutation } from 'store/api/client-identifications';
import { ClassifierData } from 'types/classifiers';
import {
    IdentityFields,
    PanelGroundsDto,
    PanelPersonDto,
    PanelTypedRowDto,
    RelatedPartiesDto,
    REPRESENTATION_GROUNDS,
    RepresentationGrounds,
} from 'types/clientIdentification';
import { PartyType } from 'types/contacts';
import { t } from 'utils/getTranslations';
import { classifierOptions, identityFieldsToForm, identityFormToFields, IdentityFormValues } from '../identityForm';
import { allRowUpdates, rowIdsTakingGrounds, withGrounds } from '../relatedPartyRows';
import { AnswerRadio } from './AnswerRadio';
import { IdentityItemInputs } from './IdentityItemInputs';
import { successAlert } from 'utils/alert';

interface FormValues extends IdentityFormValues {
    groundsCode: RepresentationGrounds | null;
    groundsNote: string;
}

export type PersonCardSubject = { kind: 'person'; person: PanelPersonDto } | { kind: 'typed'; row: PanelTypedRowDto };

interface Props {
    contractId: number;
    subject: PersonCardSubject;
    relatedParties: RelatedPartiesDto;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

const EMPTY_FIELDS: IdentityFields = {
    firstName: null,
    lastName: null,
    name: null,
    officialName: null,
    legalForm: null,
    personalCode: null,
    regCode: null,
    dateOfBirth: null,
    placeOfBirthCountryCode: null,
    placeOfBirthCity: null,
    nationalities: [],
    identificationAddress: { address1: null, address2: null, city: null, zip: null, countryCode: null },
    visitingAddress: { address1: null, address2: null, city: null, zip: null, countryCode: null },
    identityDocument: { number: null, issuingCountryCode: null, expiryDate: null },
    occupation: null,
    employmentStatusCode: null,
    natureOfBusiness: null,
    registrationDate: null,
    registrationAuthority: null,
};

/** The grounds are the client's answer and stay required; the person's own details are asked, never required. */
export function PersonCardEditModal({ contractId, subject, relatedParties, classifiers, onClose }: Props) {
    const countries = classifierOptions(classifiers['COUNTRY']);
    const employmentStatuses = classifierOptions(classifiers['EMPLOYMENT_STATUS']);
    const intl = useIntl();
    const [updateIdentity, { isLoading: isSavingIdentity }] = useUpdateIdentityMutation();
    const [updateRelatedParties, { isLoading: isSavingGrounds }] = useUpdateRelatedPartiesMutation();

    const person = subject.kind === 'person' ? subject.person : null;
    const grounds: PanelGroundsDto | null = subject.kind === 'person' ? subject.person.grounds : subject.row.grounds;
    const rowIds =
        subject.kind === 'person' ? rowIdsTakingGrounds(relatedParties, subject.person.rowIds) : [subject.row.rowId];
    const title = subject.kind === 'person' ? subject.person.name : subject.row.name;
    const subtitle =
        subject.kind === 'person' ? intl.formatList(subject.person.roleNames, { type: 'unit' }) : subject.row.roleName;

    const { control, setValue, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            ...identityFieldsToForm(person?.fields ?? EMPTY_FIELDS, countries.optionOf, employmentStatuses.optionOf),
            groundsCode: grounds?.code ?? null,
            groundsNote: grounds?.note ?? '',
        },
    });
    const groundsCode = useWatch({ control, name: 'groundsCode' });

    const submit = handleSubmit(async (values) => {
        try {
            if (person) {
                await updateIdentity({
                    id: contractId,
                    partyId: person.partyId,
                    fields: identityFormToFields(values, person.fields),
                }).unwrap();
            }
            const note = values.groundsNote.trim() || null;
            if (grounds && (values.groundsCode !== grounds.code || note !== grounds.note)) {
                await updateRelatedParties({
                    id: contractId,
                    rows: withGrounds(allRowUpdates(relatedParties), rowIds, values.groundsCode, note),
                }).unwrap();
            }
        } catch {
            // The API layer has shown the server's reason; the popup stays open with what was typed.
            return;
        }
        successAlert(t('contracts.contract-updated'));
        onClose();
    });

    const identityContext = {
        control: control as unknown as Control<IdentityFormValues>,
        setValue: setValue as unknown as SetFieldValue<IdentityFormValues>,
        partyType: person?.partyType ?? PartyType.PERSON,
        countries: countries.options,
        employmentStatuses: employmentStatuses.options,
    };

    return (
        <>
            <ModalHeader
                title={t('client-identification.edit-name', { name: title })}
                subtitle={subtitle}
                onCancel={onClose}
            />
            <form
                noValidate
                className="flex min-h-0 flex-col gap-4 overflow-y-auto p-6"
                onSubmit={(event) => void submit(event)}
            >
                {person?.itemKeys.map((key) => (
                    <IdentityItemInputs key={key} itemKey={key} context={identityContext} />
                ))}
                {grounds ? (
                    <>
                        <AnswerRadio<RepresentationGrounds>
                            label={t('client-identification.item.GROUNDS')}
                            required
                            value={groundsCode}
                            onChange={(code) => setValue('groundsCode', code, { shouldDirty: true })}
                            options={REPRESENTATION_GROUNDS.map((code) => ({
                                value: code,
                                label: t(`client-identification.grounds.${code}`),
                            }))}
                        />
                        {groundsCode === 'OTHER' ? (
                            <StarredFields value>
                                <TextInput
                                    control={control}
                                    name="groundsNote"
                                    label="client-identification.grounds-note"
                                />
                            </StarredFields>
                        ) : null}
                    </>
                ) : null}
            </form>
            <ModalFooter
                onCancel={onClose}
                onSave={() => void submit()}
                disableSave={isSavingIdentity || isSavingGrounds}
            />
        </>
    );
}
